import { formatDate } from "@khazzin/i18n/dates";
import { Icon } from "@khazzin/ui/icons/Icon";
import {
	BottomTabBar,
	INBOX_QUERY_KEY,
	NotificationBell,
	SyncStatusChip,
	TOPIC_NOTIFICATION_CREATED,
	useInboxSource,
} from "@khazzin/ui/patterns";
import {
	useEventStream,
	usePresenceHeartbeat,
	useStreamConnection,
} from "@khazzin/ui/patterns/live";
import { EmptyState, OfflinePill } from "@khazzin/ui/patterns/page";
import { useServiceWorkerUpdate } from "@khazzin/ui/patterns/serviceWorker";
import { Button } from "@khazzin/ui/primitives/Button";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { Banknote, CloudOff, History, LayoutDashboard, ListChecks, UserRound } from "lucide-react";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useApi } from "../../api";
import { useI18n } from "../../i18n";
import { AckInterstitial } from "../../modules/alerts/AckInterstitial";
import { ackQueued, awaiting } from "../../modules/alerts/alerts";
import { HANDOVERS_KEY } from "../../modules/cash/handover";
import { safeLink, type WorkerMessage } from "../../modules/push/push";
import { useOffline } from "../../offline/Offline";

/**
 * The Driver App's frame (DA-10-15, DA-10-23): a top bar with the date, the
 * sync chip and the bell, the offline banner, the tab's screen, and the tab
 * bar. Every tab reads the cached day, so the whole app works offline.
 *
 * The live stream (EP10-S22) carries this driver's stops only; a shipment's
 * hint runs a sync, so a stop dispatch adds or takes away lands at once.
 */

/** The build, reported with each presence heartbeat. */
const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? "dev";

export const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * The day is IndexedDB's, not the query cache's: a shipment hint syncs
 * instead. The bell is the driver's inbox (EP12-S15): a push no device took
 * lands there (EP12-S07), and its hint re-reads the bell.
 */
const DRIVER_TOPICS = { [TOPIC_NOTIFICATION_CREATED]: [INBOX_QUERY_KEY] };

/** The bell with no session: nothing to read. */
const quietFeed = { useFeed: () => ({ unreadCount: 0, latest: [], status: "ready" as const }) };

export function Shell() {
	const { t, locale } = useI18n();
	const {
		online,
		syncing,
		outbox,
		lastSyncAt,
		syncNow,
		day,
		alerts,
		ackAlert,
		notDriver,
		signOut,
	} = useOffline();
	const waiting = awaiting(alerts, outbox);
	const current = waiting[0];
	const navigate = useNavigate();
	// EP10-S23: a newer build, checked on every navigation, as the portal's bar
	// is. Profile had the only Update now, and a driver who never opens it
	// kept the old build until every tab was closed.
	const update = useServiceWorkerUpdate(useLocation().pathname);
	const queued = outbox.filter((e) => e.state === "queued").length;
	const api = useApi();
	const queryClient = useQueryClient();
	// Signed in and on the network: the cached day opens without either.
	const signedIn = useAuth().user !== null;
	const live = signedIn && online;
	usePresenceHeartbeat(
		api,
		() =>
			lastSyncAt === null
				? { interface: "driver", appVersion: APP_VERSION }
				: { interface: "driver", appVersion: APP_VERSION, lastSyncAt },
		live,
	);
	const stream = useEventStream(api, DRIVER_TOPICS, {
		enabled: live,
		onEvent: (event) => {
			if (event.topic.startsWith("shipment.")) {
				syncNow();
			}
			// EP10-S20: the desk closed this driver's handover. The wallet drops the cash.
			if (event.topic === "cod_handover.confirmed") {
				syncNow();
				void queryClient.invalidateQueries({ queryKey: HANDOVERS_KEY });
			}
		},
	});
	const connection = useStreamConnection(stream);
	const inbox = useInboxSource(api);
	// EP10-S21: the push handler (public/push-sw.js) asks the open app to sync —
	// the prompt comes from the sync — and routes a tapped banner here.
	useEffect(() => {
		const worker = navigator.serviceWorker;
		if (worker === undefined) {
			return;
		}
		const onMessage = (event: MessageEvent<WorkerMessage | undefined>) => {
			if (event.data?.type === "khazzin.push") {
				syncNow();
			} else if (event.data?.type === "khazzin.open") {
				void navigate(safeLink(event.data.link));
			}
		};
		worker.addEventListener("message", onMessage);
		return () => worker.removeEventListener("message", onMessage);
	}, [syncNow, navigate]);

	// Signed in, but nobody made this account a driver: the Store Portal's
	// "No store yet", rather than a day that loads forever.
	if (notDriver) {
		return (
			<main className="grid min-h-screen place-items-center bg-bg-canvas px-5">
				<EmptyState
					title={t("driver.shell.notDriverTitle")}
					body={t("driver.shell.notDriverBody")}
					action={
						<Button className="min-h-12" onClick={() => void signOut()}>
							{t("driver.profile.signOut")}
						</Button>
					}
				/>
			</main>
		);
	}

	return (
		<div className="flex min-h-screen flex-col bg-bg-canvas">
			<header className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-bg-surface px-4 py-2">
				<p className="me-auto text-[14px] font-bold text-fg">
					{formatDate(new Date(), { locale, timeZone })}
				</p>
				<SyncStatusChip
					state={online ? (syncing ? "syncing" : "online") : "offline"}
					queued={queued}
					t={t}
					onOpen={() => void navigate("/profile")}
				/>
				<NotificationBell
					// Signed out (offline, the cache still opens) there is no inbox to
					// read; the key remounts the bell on the switch.
					key={signedIn ? "inbox" : "quiet"}
					source={signedIn ? inbox : quietFeed}
					t={t}
					locale={locale}
					timeZone={timeZone}
					onNavigate={(to) => void navigate(to)}
				/>
			</header>
			{update.available && (
				<p
					role="status"
					className="flex items-center gap-2 border-b border-info-border bg-info-bg px-4 py-2 text-[13px] text-info-fg"
				>
					<span className="me-auto">{t("driver.profile.updateAvailable")}</span>
					<Button size="sm" onClick={update.apply}>
						{t("common.upgrade.updateNow")}
					</Button>
				</p>
			)}
			{!online && (
				<p
					role="status"
					className="flex items-center gap-2 bg-warning-bg px-4 py-2 text-[13px] text-warning-fg"
				>
					<Icon as={CloudOff} size={16} />
					{t("driver.shell.offline")}
				</p>
			)}
			{!online && ackQueued(outbox) && (
				<p role="status" className="bg-bg-subtle px-4 py-2 text-[13px] text-fg-muted">
					{t("driver.alerts.queued")}
				</p>
			)}
			{current !== undefined && (
				<AckInterstitial
					alert={current}
					stop={current.shipmentId === undefined ? undefined : day?.shipments[current.shipmentId]}
					more={waiting.length - 1}
					t={t}
					onAcknowledge={(a) => ackAlert(a.id)}
				/>
			)}
			<main className="flex-1 px-4 py-4">
				<Outlet />
			</main>
			<OfflinePill status={connection} t={t} />
			<BottomTabBar
				label={t("driver.shell.tabs")}
				tabs={[
					{ to: "/home", label: t("driver.shell.home"), icon: LayoutDashboard },
					{ to: "/today", label: t("driver.shell.today"), icon: ListChecks },
					{ to: "/history", label: t("driver.shell.history"), icon: History },
					{ to: "/cash", label: t("driver.shell.cash"), icon: Banknote },
					{ to: "/profile", label: t("driver.shell.profile"), icon: UserRound },
				]}
			/>
		</div>
	);
}
